import type {FC} from 'react'
import {Flash, Heading, Link, LinkButton} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {AlertIcon, GitBranchIcon} from '@primer/octicons-react'
import type {SourceType} from '../types/rules-types'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {NewRulesetButton} from '../components/NewRulesetButton'
import {useRelativeNavigation} from '../hooks/use-relative-navigation'
import type {FlashAlert} from '@github-ui/dismissible-flash'
import styles from './UpsellBanner.module.css'
import {useUpsellInfoContext} from '../contexts/UpsellInfoContext'
import {useRuleStrings} from '../hooks/use-rule-strings'

type UpsellBannerProps = {
  ctaType: 'rulesets' | 'enterpriseRulesets'
  sourceType: SourceType
}

export const FlashUpsellBanner: FC<UpsellBannerProps> = ({ctaType, sourceType}) => {
  const {
    askAdmin,
    [ctaType]: {
      cta: {path: ctaPath},
    },
    organization,
  } = useUpsellInfoContext()
  const button = true
  const bannerProps = {organization, askAdmin, ctaPath, button, sourceType}
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const category = 'rulesets.upsell'
  const action = 'RULESETS_UPSELL_BUTTON_CLICKED'
  return (
    <Flash variant="warning" className="d-flex flex-items-center flex-justify-between">
      <UpsellBannerText {...bannerProps} />
      {!askAdmin && (
        <LinkButton
          className="tmp-ml-5"
          href={ctaPath}
          onClick={() =>
            sendClickAnalyticsEvent({
              category,
              action,
              label: 'RULESET_LEARN_MORE_LINK',
            })
          }
        >
          Upgrade
        </LinkButton>
      )}
    </Flash>
  )
}

const UpsellBannerText = ({
  organization,
  askAdmin,
  ctaPath,
  button,
  sourceType,
}: {
  organization: boolean
  askAdmin: boolean
  ctaPath?: string
  button?: boolean
  sourceType: SourceType
}) => {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const category = 'rulesets.upsell'
  const action = 'RULESETS_UPGRADE_LINK_CLICKED'
  let message = ''
  let linkText = ''
  if (sourceType === 'organization') {
    message = "Organization rulesets won't be enforced "
    linkText = 'until you upgrade this organization account to GitHub Team.'
  } else {
    message = "Your rulesets won't be enforced on this private repository until"
    if (!askAdmin) {
      message = message.concat(' you ')
      linkText = organization
        ? 'upgrade this organization account to GitHub Team.'
        : 'move to GitHub Team organization account.'
    } else {
      message = message.concat(' your ')
      linkText = 'organization account admins upgrade this organization account to GitHub Team.'
    }
  }

  return (
    <div className="d-flex my-2">
      <div>
        <Octicon icon={AlertIcon} className={styles.UpsellBannerOcticon} />
      </div>
      {button ? (
        <>
          {message}
          {linkText}
        </>
      ) : (
        <div className={'color-fg-muted'}>
          {askAdmin ? (
            <>
              {message}
              {linkText}
            </>
          ) : (
            <>
              {message}
              <Link
                href={ctaPath}
                onClick={() =>
                  sendClickAnalyticsEvent({
                    category,
                    action,
                    label: 'RULESETS_UPSELL_TEXT_LINK',
                  })
                }
              >
                {linkText}
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export const UpsellBanner: FC<{
  headingText: string
  withBorder: boolean
  showNewRulesetButton: boolean
  ctaType: 'rulesets' | 'enterpriseRulesets'
  infoVariant: 'one' | 'two'
  sourceType: SourceType
  setFlashAlert: (flashAlert: FlashAlert) => void
}> = ({headingText, withBorder, showNewRulesetButton, ctaType, infoVariant, sourceType, setFlashAlert}) => {
  const borderProps = 'd-md-flex border rounded-2 color-border-default p-3 mb-3'
  const noBorderProps = 'd-flex'
  const {
    askAdmin,
    [ctaType]: {cta},
    organization,
  } = useUpsellInfoContext()
  const upsellBannerProps = {askAdmin, ctaPath: cta.path, organization, sourceType}
  const {resolvePath} = useRelativeNavigation()

  return (
    <div className={withBorder ? borderProps : noBorderProps}>
      <div className="tmp-mr-3">
        <span className="branch-action-item-icon completeness-indicator completeness-indicator-problem float-none ml-0">
          <Octicon icon={GitBranchIcon} />
        </span>
      </div>
      <div>
        <Heading as="h2" className={styles.UpsellBannerHeading}>
          {headingText}
        </Heading>
        <InfoVariants infoVariant={infoVariant} />
        {cta.visible && <UpsellBannerText {...upsellBannerProps} />}
        {showNewRulesetButton && <NewRulesetButton rulesetsUrl={resolvePath('./')} setFlashAlert={setFlashAlert} />}
      </div>
    </div>
  )
}

function InfoVariants({infoVariant}: {infoVariant: 'one' | 'two'}) {
  const documentationHref = `https://docs.github.com/repositories/configuring-branches-and-merges-in-your-repository/managing-rulesets/about-rulesets`
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const category = 'rulesets.learn_more'
  const action = 'RULESETS_INFO_LINK_CLICKED'

  const {upsellInfoMessage} = useRuleStrings()
  const message = infoVariant === 'one' ? ` define ${upsellInfoMessage}` : `Define ${upsellInfoMessage} `

  return (
    <p className={'color-fg-muted'}>
      {infoVariant === 'one' ? (
        <>
          <Link
            href={documentationHref}
            onClick={() =>
              sendClickAnalyticsEvent({
                category,
                action,
                label: 'RULESETS_DOC_LINK',
              })
            }
            inline
          >
            Rulesets
          </Link>
          {message}
        </>
      ) : (
        <>
          {message}
          <Link
            href={documentationHref}
            onClick={() =>
              sendClickAnalyticsEvent({
                category,
                action,
                label: 'RULESET_LEARN_MORE_LINK',
              })
            }
            inline
          >
            Learn more about rulesets.
          </Link>
        </>
      )}
    </p>
  )
}
